import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Plus, GraduationCap, MapPin, Clock, Users, Search, Calendar } from "lucide-react"
import { useTrainingSessions } from "@/hooks/use-training"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import type { TrainingSession, TrainingStatus, TrainingType } from "@/lib/types"

const TYPE_LABELS: Record<TrainingType, string> = {
  internal: "Wewnętrzne",
  external: "Zewnętrzne",
  online: "Online",
  certification: "Certyfikacja",
}

const STATUS_CONFIG: Record<TrainingStatus, { label: string; color: string }> = {
  planned: { label: "Zaplanowane", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  in_progress: { label: "W trakcie", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
  completed: { label: "Zakończone", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
  cancelled: { label: "Anulowane", color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300" },
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

function TrainingCard({ session }: { session: TrainingSession }) {
  const navigate = useNavigate()
  const statusCfg = STATUS_CONFIG[session.status]
  const isPast = new Date(session.ends_at) < new Date()

  return (
    <div
      className="surface-card p-4 cursor-pointer transition-shadow hover:shadow-md"
      onClick={() => navigate(`/training/${session.id}`)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 mr-3">
          <h3 className="font-semibold truncate text-foreground">{session.name}</h3>
          <p className="text-sm mt-0.5 text-muted-foreground">
            {TYPE_LABELS[session.training_type]}
            {session.instructor_name && ` · ${session.instructor_name}`}
          </p>
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusCfg.color}`}>
          {statusCfg.label}
        </span>
      </div>

      <div className="space-y-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{formatDateShort(session.starts_at)}</span>
          {session.duration_hours && <span>· {session.duration_hours}h</span>}
        </div>
        {session.location && (
          <div className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{session.location}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Users className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            {session.participants_count} uczestników
            {session.max_participants && ` / ${session.max_participants}`}
          </span>
        </div>
      </div>

      {session.cost_per_person && (
        <div className="mt-3 pt-3 border-t">
          <span className="text-sm font-medium tabular-nums text-foreground">
            {new Intl.NumberFormat("pl-PL", { style: "currency", currency: session.currency || "PLN" }).format(session.cost_per_person)} / os.
          </span>
        </div>
      )}
    </div>
  )
}

export function TrainingListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [typeFilter, setTypeFilter] = useState<string>("all")

  const filters = {
    q: search || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    training_type: typeFilter !== "all" ? typeFilter : undefined,
  }

  const { data: sessionsData, isLoading } = useTrainingSessions(filters)
  const sessions = sessionsData?.data ?? []

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Szkolenia</h1>
          <p className="text-sm mt-1 text-muted-foreground">
            Zarządzaj szkoleniami i certyfikatami zespołu
          </p>
        </div>
        <Button onClick={() => navigate("/training/new")}>
          <Plus className="h-4 w-4 mr-2" />
          Nowe szkolenie
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj szkoleń..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie statusy</SelectItem>
            <SelectItem value="planned">Zaplanowane</SelectItem>
            <SelectItem value="in_progress">W trakcie</SelectItem>
            <SelectItem value="completed">Zakończone</SelectItem>
            <SelectItem value="cancelled">Anulowane</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Typ" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Wszystkie typy</SelectItem>
            <SelectItem value="internal">Wewnętrzne</SelectItem>
            <SelectItem value="external">Zewnętrzne</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="certification">Certyfikacja</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[180px] rounded-lg" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-16">
          <GraduationCap className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-semibold mb-1 text-foreground">Brak szkoleń</h3>
          <p className="text-sm mb-4 text-muted-foreground">
            Utwórz pierwsze szkolenie dla zespołu
          </p>
          <Button onClick={() => navigate("/training/new")}>
            <Plus className="h-4 w-4 mr-2" />
            Nowe szkolenie
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((session) => (
            <TrainingCard key={session.id} session={session} />
          ))}
        </div>
      )}
    </div>
  )
}
